import { useState } from "react";
import { KeyRound } from "lucide-react";
import { getAdminTradovateReauth, useQuery } from "wasp/client/operations";
import { Card, CardContent } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";

const DEFAULT_HOURS = 48;
type AllowedHours = 24 | 48 | 168 | 720;

/**
 * B2 : la ré-autorisation du socket sur place tient-elle ?
 *
 * Le verdict par connexion est la seule lecture qui compte : « Session
 * prouvée » veut dire qu'une requête est passée par le socket APRÈS
 * l'expiration de l'ancien jeton. L'absence de coupure ne prouve rien (un
 * socket peut garder son heartbeat et ne plus rien pousser).
 */
const TradovateReauthCard = () => {
  const t = useTranslation();
  const [hours, setHours] = useState<AllowedHours>(DEFAULT_HOURS);
  const { data } = useQuery(getAdminTradovateReauth, { hours });

  const ranges: { value: AllowedHours; label: string }[] = [
    { value: 24, label: t.adminDashboard.quotaHistoryRange24h },
    { value: 48, label: t.adminDashboard.quotaHistoryRange48h },
    { value: 168, label: t.adminDashboard.quotaHistoryRange7d },
    { value: 720, label: t.adminDashboard.quotaHistoryRange30d },
  ];

  const rows = (data?.rows ?? []) as Array<{
    connectionId: string;
    connectionLabel: string;
    userEmail: string;
    propFirmName: string | null;
    reauthOk: number;
    reauthFailed: number;
    proofOk: number;
    proofFailed: number;
    lastEventAt: Date | null;
    verdict: string;
  }>;

  const verdictLabel = (v: string): string => {
    switch (v) {
      case "proven":
        return t.adminDashboard.reauthVerdictProven;
      case "awaiting_proof":
        return t.adminDashboard.reauthVerdictAwaiting;
      case "failing":
        return t.adminDashboard.reauthVerdictFailing;
      default:
        return t.adminDashboard.reauthVerdictNoActivity;
    }
  };
  const verdictClass = (v: string): string => {
    switch (v) {
      case "proven":
        return "bg-pnlPositive/15 text-pnlPositive";
      case "failing":
        return "bg-pnlNegative/15 text-pnlNegative";
      case "awaiting_proof":
        return "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]";
      default:
        return "bg-accent text-muted-foreground";
    }
  };

  // « ok / échec » : l'échec en rouge dès qu'il existe.
  const pair = (ok: number, failed: number) => (
    <>
      <span className="text-foreground">{ok}</span>
      <span className="text-muted-foreground">{" / "}</span>
      <span className={failed > 0 ? "text-pnlNegative font-semibold" : "text-muted-foreground"}>
        {failed}
      </span>
    </>
  );

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <KeyRound className="h-4 w-4" />
        {t.adminDashboard.reauthTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">{t.adminDashboard.reauthHint}</p>

      <Card>
        <CardContent className="pt-4">
          {!data?.enabled ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {t.adminDashboard.reauthDisabled}
            </p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">
                  {t.adminDashboard.reauthUsers}{" "}
                  <span className="text-foreground font-mono font-semibold">
                    {Number(data.enabledUserCount)}
                  </span>
                </span>
                <div className="flex gap-1">
                  {ranges.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setHours(r.value)}
                      className={`rounded px-2 py-1 text-xs transition-colors ${
                        hours === r.value
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-muted-foreground mb-3 text-[10px] italic">
                {t.adminDashboard.reauthCountsHint}
              </p>
              {rows.length === 0 ? (
                <p className="text-muted-foreground py-6 text-center text-sm">
                  {t.adminDashboard.reauthEmpty}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground border-border border-b text-left text-xs">
                        <th className="pb-2 font-normal">{t.adminDashboard.reauthColUser}</th>
                        <th className="pb-2 font-normal">{t.adminDashboard.reauthColPropFirm}</th>
                        <th className="pb-2 font-normal">{t.adminDashboard.reauthColVerdict}</th>
                        <th className="pb-2 text-right font-normal">
                          {t.adminDashboard.reauthColReauth}
                        </th>
                        <th className="pb-2 text-right font-normal">
                          {t.adminDashboard.reauthColProof}
                        </th>
                        <th className="pb-2 text-right font-normal">
                          {t.adminDashboard.reauthColLast}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.connectionId} className="border-border/50 border-b">
                          <td className="text-foreground py-2">
                            <span className="flex items-center gap-2">
                              <span className="truncate">{r.userEmail}</span>
                              <span className="text-muted-foreground bg-accent shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]">
                                {r.connectionLabel}
                              </span>
                            </span>
                          </td>
                          <td className="text-muted-foreground py-2 text-xs">
                            {r.propFirmName ?? "-"}
                          </td>
                          <td className="py-2">
                            <span className={`rounded px-1.5 py-0.5 text-[10px] ${verdictClass(r.verdict)}`}>
                              {verdictLabel(r.verdict)}
                            </span>
                          </td>
                          <td className="py-2 text-right font-mono text-xs">
                            {pair(Number(r.reauthOk), Number(r.reauthFailed))}
                          </td>
                          <td className="py-2 text-right font-mono text-xs">
                            {pair(Number(r.proofOk), Number(r.proofFailed))}
                          </td>
                          <td className="text-muted-foreground py-2 text-right font-mono text-xs">
                            {r.lastEventAt ? new Date(r.lastEventAt).toLocaleString() : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateReauthCard;
